import { uniq } from 'lodash';
import {
  type AssetTypeEnum,
  type DetailedProxyMetadata,
  type Fund,
  type InvestmentSourceTypeEnum,
  type Portfolio,
  type SimpleFund,
  type VdparFundMetadata,
  getCompositeBenchmarkByFundId,
  getFund,
} from 'venn-api';
import type { Images } from 'venn-ui-kit';
import type { FeatureName } from 'venn-utils';

import { getAppTitle } from '../../../venn-ui-kit/src/themes/src/getAppTitle';
import { getTextThemeProvider } from '../../../venn-ui-kit/src/themes/src/getTextThemeProvider';
import type { StudioRequestSubject } from '../../../../venn-state/src/studio/types';
import { VennQueryClient } from '../queries/vennQueryClient';
import { createCompositeBenchmarkKey, createGetFundKey } from '../queries/query-keys';
import { getAppTitleSimplifiedUtil } from '../utils';

/**
 * Only user uploaded funds can be edited.
 */
export const isPerformanceEditable = (fund?: Fund) => {
  return fund?.assetType !== 'BENCHMARK' && fund?.userUploaded;
};

/**
 * Both User uploaded and Custodian fund data can be viewed.
 */
export const isPerformanceViewable = (fund?: Fund) => {
  return isPerformanceEditable(fund) || isViewableUserIntegration(fund?.investmentSource);
};

/**
 * Both User uploaded and Custodian fund data can be exported.
 */
export const isPerformanceExportable = isPerformanceViewable;

/**
 * User integrations are determined by investmentSource.
 * There are investments whose remoteType may be EXTERNAL but are NOT user-owned data
 * and shouldn't be treated as an integration, so the remoteType field shouldn't be used.
 */
export const isUserIntegration = (investmentSource?: InvestmentSourceTypeEnum) => {
  return isViewableUserIntegration(investmentSource) || investmentSource === 'EXTERNAL_BYOL';
};

/**
 * Whether an investment of a particular source is a user integration whose performance is viewable.
 */
const isViewableUserIntegration = (investmentSource?: InvestmentSourceTypeEnum) => {
  return investmentSource === 'CUSTODIAN' || investmentSource === 'EXTERNAL_INTEGRATION';
};

type AssetTypeConfig = {
  name: string;
  tooltip?: string;
  badge?: {
    featureFlag?: FeatureName;
    getIcon: (images: Images) => string | undefined;
  };
};

const ASSET_TYPE_CONFIG: Record<AssetTypeEnum, AssetTypeConfig> = {
  CATEGORY_GROUPING: {
    name: `${getAppTitleSimplifiedUtil()} Category`,
    tooltip: `Investment categories are non\u2011investable equal\u2011weighted indices created by ${getAppTitle()} from investments in the same mutual fund, ETF, or hedge fund category.`,
  },
  VENN_DAILY_PRIVATE_ASSET_RETURNS: {
    name: getTextThemeProvider().vdparName,
    tooltip: 'Returns modeled using cohort cash flow data and a\u00A0public market proxy.',
    badge: {
      featureFlag: 'venn_daily_private_asset_returns_ff',
      getIcon: (images: Images) => images.vennDailyPrivateAssetReturns,
    },
  },
  BENCHMARK: { name: 'Composite Benchmark' },
  CURRENCY: { name: 'Cash Rate' },
  CUSTODIAN_ACCOUNT: { name: 'Custodian Account' },
  ETF: { name: 'ETF' },
  EXTERNAL_INTEGRATION_INVESTMENT: { name: 'External Integration Investment' },
  INDEX: { name: 'Index' },
  MANAGER: { name: 'Hedge Fund' },
  MUTUAL_FUND: { name: 'Mutual Fund' },
  STOCK: { name: 'Stock' },
  UCIT: { name: 'UCITS' },
  UPLOADED_INSTRUMENT: { name: 'User Uploaded' },
  CRYPTO_REFERENCE_RATE: { name: 'Crypto Reference Rate' },
  OPTION: { name: 'Option' },
};

export const getAssetTypeConfig = (assetType: AssetTypeEnum) => {
  return ASSET_TYPE_CONFIG[assetType] ?? { name: '--' };
};

export const getAssetTypeName = (assetType: AssetTypeEnum) => {
  return ASSET_TYPE_CONFIG[assetType]?.name ?? '--';
};

/**
 * Checks if a fund is a direct VDPAR (not a composite benchmark containing VDPAR).
 * This is a synchronous check based on asset type only.
 */
export const isDirectVdpar = (fund?: Fund | SimpleFund): boolean => {
  return fund?.assetType === 'VENN_DAILY_PRIVATE_ASSET_RETURNS';
};

const getVdparMetadataFromCompositeBenchmark = async (
  compositeBenchmarkFundId: string,
): Promise<VdparFundMetadata[]> => {
  const queryClient = VennQueryClient.getInstance();
  const result = await queryClient
    .fetchQuery({
      queryKey: createCompositeBenchmarkKey(compositeBenchmarkFundId),
      queryFn: () => getCompositeBenchmarkByFundId(compositeBenchmarkFundId),
    })
    .catch(() => null);
  return result?.content?.vdparFundMetadata ?? [];
};

const containsVdparInCompositeBenchmark = async (compositeBenchmarkFundId: string): Promise<boolean> => {
  const metadata = await getVdparMetadataFromCompositeBenchmark(compositeBenchmarkFundId);
  return metadata.length > 0;
};

/**
 * Checks if a fund is or contains VDPAR (Venn Daily Private Asset Returns).
 * For direct VDPAR funds, checks asset type. For composite benchmarks, queries the API.
 * @internal Exported for testing purposes. Use subjectContainsVennDailyPrivateAssetReturn or getVennDailyPrivateAssetReturnsFromSubject instead.
 */
export const isVennDailyPrivateAssetReturn = async (fund?: Fund | SimpleFund): Promise<boolean> => {
  if (!fund) return false;
  if (isDirectVdpar(fund)) return true;
  return fund.assetType === 'BENCHMARK' && !!fund.id && containsVdparInCompositeBenchmark(fund.id);
};

const getVdparMetadataFromFund = async (fund: Fund | SimpleFund | undefined): Promise<VdparFundMetadata[]> => {
  if (!fund) return [];
  if (isDirectVdpar(fund) && fund.id) return [{ id: fund.id, name: fund.name }];
  if (fund.assetType === 'BENCHMARK' && fund.id) {
    return getVdparMetadataFromCompositeBenchmark(fund.id);
  }
  return [];
};

const getVdparMetadataInPortfolio = async (portfolio: Portfolio): Promise<VdparFundMetadata[]> => {
  const traverse = async (node: Portfolio): Promise<VdparFundMetadata[]> => {
    const current = await getVdparMetadataFromFund(node.fund);
    const children = (await Promise.all((node.children ?? []).map(traverse))).flat();
    return [...current, ...children];
  };
  return traverse(portfolio);
};

/** @internal Exported for testing purposes. Use getVennDailyPrivateAssetReturnsFromSubject instead. */
export const getVennDailyPrivateAssetNamesInPortfolio = async (portfolio: Portfolio): Promise<string[]> => {
  const metadata = await getVdparMetadataInPortfolio(portfolio);
  return uniq(metadata.map((m) => m.name)).sort();
};

export const getVennDailyPrivateAssetReturnsFromSubject = async (subject: StudioRequestSubject): Promise<string[]> => {
  if (subject.subjectType === 'INVESTMENT') {
    const fund = subject.fund ?? subject.strategy?.fund;
    const metadata = await getVdparMetadataFromFund(fund);
    return metadata.map((m) => m.name);
  }
  if (subject.subjectType === 'PORTFOLIO' && subject.portfolio) {
    return getVennDailyPrivateAssetNamesInPortfolio(subject.portfolio);
  }
  return [];
};

export const subjectContainsVennDailyPrivateAssetReturn = async (subject: StudioRequestSubject): Promise<boolean> => {
  const names = await getVennDailyPrivateAssetReturnsFromSubject(subject);
  return names.length > 0;
};

/** @internal Exported for testing purposes. Use getVennDailyPrivateAssetFundIdsFromSubject instead. */
export const getVennDailyPrivateAssetFundIdsInPortfolio = async (portfolio: Portfolio): Promise<string[]> => {
  const metadata = await getVdparMetadataInPortfolio(portfolio);
  return uniq(metadata.map((m) => m.id));
};

export const getVennDailyPrivateAssetFundIdsFromSubject = async (subject: StudioRequestSubject): Promise<string[]> => {
  if (subject.subjectType === 'INVESTMENT') {
    const fund = subject.fund ?? subject.strategy?.fund;
    const metadata = await getVdparMetadataFromFund(fund);
    return metadata.map((m) => m.id);
  }
  if (subject.subjectType === 'PORTFOLIO' && subject.portfolio) {
    return getVennDailyPrivateAssetFundIdsInPortfolio(subject.portfolio);
  }
  return [];
};

/**
 * Extracts all proxy IDs from DetailedProxyMetadata, including both the primary proxyId
 * and any secondary proxies in proxyMetaList (used for layered proxies like interpolation/extrapolation).
 */
export const extractAllProxyIds = (metadataList: DetailedProxyMetadata[]): string[] => {
  return metadataList
    .flatMap((meta) => [meta.proxyId, ...(meta.proxyMetaList ?? []).map((pm) => pm.proxyId)])
    .filter((id): id is string => Boolean(id));
};

/**
 * Gets VDPAR names from proxy metadata by fetching fund info for each proxy.
 * Handles both direct VDPAR proxies and composite benchmark proxies containing VDPAR.
 * Also extracts VDPAR from secondary proxies in proxyMetaList (layered proxies).
 * Uses VennQueryClient for caching to avoid redundant API calls.
 */
export const getVdparNamesFromProxyMetadata = async (
  proxyMetadataList: (DetailedProxyMetadata | null | undefined)[],
): Promise<string[]> => {
  const validMetadata = proxyMetadataList.filter(Boolean) as DetailedProxyMetadata[];
  const uniqueProxyIds = uniq(extractAllProxyIds(validMetadata));

  const queryClient = VennQueryClient.getInstance();

  const fundResults = await Promise.all(
    uniqueProxyIds.map((proxyId) =>
      queryClient
        .fetchQuery({
          queryKey: createGetFundKey(proxyId),
          queryFn: () => getFund(proxyId),
        })
        .catch(() => null),
    ),
  );

  const vdparMetadataResults = await Promise.all(
    fundResults.map((result) => (result?.content ? getVdparMetadataFromFund(result.content) : [])),
  );

  const allVdparNames = vdparMetadataResults.flat().map((m) => m.name);
  return uniq(allVdparNames);
};

/**
 * Returns a Set of proxy IDs that are or contain VDPAR funds.
 * Handles both direct VDPAR proxies and composite benchmark proxies.
 * Also checks secondary proxies in proxyMetaList (layered proxies).
 */
export const getVdparProxyIds = async (
  proxyMetadataList: (DetailedProxyMetadata | null | undefined)[],
): Promise<Set<string>> => {
  const validMetadata = proxyMetadataList.filter(Boolean) as DetailedProxyMetadata[];
  const uniqueProxyIds = uniq(extractAllProxyIds(validMetadata));

  const queryClient = VennQueryClient.getInstance();

  const fundResults = await Promise.all(
    uniqueProxyIds.map((proxyId) =>
      queryClient
        .fetchQuery({
          queryKey: createGetFundKey(proxyId),
          queryFn: () => getFund(proxyId),
        })
        .catch(() => null),
    ),
  );

  const vdparChecks = await Promise.all(
    uniqueProxyIds.map(async (proxyId, index) => {
      const fundResult = fundResults[index];
      if (!fundResult?.content) return null;
      const containsVdpar = await isVennDailyPrivateAssetReturn(fundResult.content);
      return containsVdpar ? proxyId : null;
    }),
  );

  return new Set(vdparChecks.filter(Boolean) as string[]);
};
